import {Fragment, ReactElement, useEffect, useMemo, useState} from "react";
import {Col, Grid, Row} from "@zendeskgarden/react-grid";
import * as css from "./styles/payrollSettings.module.scss";
import {Dropdown, Field, Item, Label, Menu, Select} from "@zendeskgarden/react-dropdowns";
import {Hint, Label as FormLabel} from "@zendeskgarden/react-forms";
import {Button} from "@zendeskgarden/react-buttons";
import {Body, Cell, Head, HeaderCell, HeaderRow, Row as TableRow, Table} from "@zendeskgarden/react-tables";
import {addDays, generatePayDates, getPayPeriodsPerMonth, PayPeriodsPerMonth} from "./logic/payroll-dates";
import {
    PayrollPayFrequency,
    useGetPayrollClientConfigurationSettingLazyQuery,
    useSetPayrollClientConfigurationSettingMutation
} from "../../__generated__/generated_types";
import {ensureDateInUtc} from "../../utils/helper-funcs";
import {useProperties} from "../../contexts/properties/PropertiesContext";
import {toast} from "react-toastify";
import {Alert, Title} from "@zendeskgarden/react-notifications";
import {CustomPayDatesEditor} from "./CustomPayDatesEditor";
import {
    buildCustomPresetDates,
    getDatesByMonth,
    getMonthReferences,
    sortAndDedupeDates,
    toDateKey
} from "./logic/payroll-custom-dates";
import ConfirmationModal from "../../components/confirmation-modal/ConfirmationModal";

const payFrequencyLabel = (frequency: PayrollPayFrequency) => {
    switch(frequency) {
        case PayrollPayFrequency.Weekly:
            return "Weekly";
        case PayrollPayFrequency.BiWeekly:
            return "Bi-Weekly";
        case PayrollPayFrequency.SemiMonthly:
            return "Semi-Monthly";
        case PayrollPayFrequency.Monthly:
            return "Monthly";
        case PayrollPayFrequency.Custom:
            return "Custom Pay Dates";
        default:
            return frequency;
    }
};

const customPresetFrequencies: PayrollPayFrequency[] = [
    PayrollPayFrequency.Weekly,
    PayrollPayFrequency.BiWeekly,
    PayrollPayFrequency.SemiMonthly,
    PayrollPayFrequency.Monthly
];
function isDefined<T>(value: T | undefined): value is T {
    return value !== undefined;
}

export function PayrollFrequencySettings(): ReactElement {
    const [fetchCurrent, {data: currentSettingData, loading: currentSettingLoading}] = useGetPayrollClientConfigurationSettingLazyQuery({
        fetchPolicy: "no-cache"
    });

    const [updateSetting, {loading: saving}] = useSetPayrollClientConfigurationSettingMutation({
        notifyOnNetworkStatusChange: true,
        onCompleted: () => {
            loadCurrent();
            toast.success("Payroll settings updated");
        },
        onError: () => {
            toast.error("Failed to update payroll settings");
        }
    });

    const {currentProperty} = useProperties();
    const [payFrequency, setPayFrequency] = useState<PayrollPayFrequency>();
    const [payPeriodsMonthly, setPayPeriodsMonthly] = useState<PayPeriodsPerMonth>();
    const [customPayDates, setCustomPayDates] = useState<Date[]>([]);
    const [currentSetting, setCurrentSetting] = useState<{payFrequency: PayrollPayFrequency, payPeriodsMonthly: PayPeriodsPerMonth | undefined, customPayDateKeys: string[]} | undefined>();
    const [seedDate, setSeedDate] = useState<Date>();
    const [showSaveConfirmationWarning, setShowSaveConfirmationWarning] = useState(false);

    function loadCurrent() {
        if(!budgetYear) {
            return;
        }
        fetchCurrent({
            variables: {
                budgetYear: budgetYear
            }
        });
    }

    function saveChanges() {
        if(!budgetYear || !payFrequency) {
            return;
        }
        updateSetting({
            variables: {
                budgetYear: budgetYear,
                payFrequency: payFrequency,
                payDateCalculationSeed: payFrequency == PayrollPayFrequency.Weekly || payFrequency == PayrollPayFrequency.BiWeekly ? seedDate : undefined,
                customPayDates: payFrequency == PayrollPayFrequency.Custom ? sortAndDedupeDates(customPayDates).map(date => date.toISOString()) : undefined
            }
        });
    }

    const budgetYear = useMemo(() => currentProperty?.budgetYear, [currentProperty]);

    const monthReferences = useMemo(() => budgetYear ? getMonthReferences(budgetYear) : [], [budgetYear]);
    const customDatesByMonth = useMemo(() => budgetYear ? getDatesByMonth(customPayDates, budgetYear) : [], [customPayDates, budgetYear]);
    const customDateKeys = useMemo(() => sortAndDedupeDates(customPayDates).map(toDateKey), [customPayDates]);

    const haveChanges = useMemo(() => {
        if(!currentSetting && !payFrequency && !currentSettingLoading && !currentSettingData) {
            return false;
        }
        if(!currentSetting) {
            return true;
        }
        if(currentSetting.payFrequency != payFrequency) {
            return true;
        }
        if(payFrequency == PayrollPayFrequency.Custom) {
            return currentSetting.customPayDateKeys.length != customDateKeys.length
                || currentSetting.customPayDateKeys.some((value, index) => value != customDateKeys[index]);
        }
        if(payFrequency == PayrollPayFrequency.Weekly || payFrequency == PayrollPayFrequency.BiWeekly) {
            if(!currentSetting.payPeriodsMonthly && payPeriodsMonthly || currentSetting.payPeriodsMonthly && !payPeriodsMonthly) {
                return true;
            }
            if(currentSetting.payPeriodsMonthly && payPeriodsMonthly) {
                return currentSetting.payPeriodsMonthly.budget.some((v, i) => v != payPeriodsMonthly.budget[i])
                    || currentSetting.payPeriodsMonthly.reforecast.some((v, i) => v != payPeriodsMonthly.reforecast[i]);
            }
        }
        return false;
    }, [payFrequency, seedDate, currentSetting, payPeriodsMonthly, customDateKeys]);

    function moveSeedDate(seedDate: Date, days: number) {
        if(!budgetYear) {
            return;
        }
        let newSeedDate = addDays(seedDate, days);
        if(payFrequency == PayrollPayFrequency.Weekly || payFrequency == PayrollPayFrequency.BiWeekly) {
            let generated = generatePayDates(payFrequency, budgetYear - 1, newSeedDate);
            let payPeriodsMonthly = getPayPeriodsPerMonth(budgetYear - 1, generated[0]);
            // The while loop below ensures that we have same number of pay cycles each year when the seed date is moved.
            while(payPeriodsMonthly.reforecast.sum() != payPeriodsMonthly.budget.sum()) {
                newSeedDate = addDays(newSeedDate, days);
                generated = generatePayDates(payFrequency, budgetYear - 1, newSeedDate);
                payPeriodsMonthly = getPayPeriodsPerMonth(budgetYear - 1, generated[0]);
            }
            setSeedDate(newSeedDate);
        }
    }

    useEffect(() => {
        loadCurrent();
    }, [currentProperty?.budgetYear]);

    useEffect(() => {
        if(!currentSettingData || currentSettingLoading) {
            return;
        }
        const currentSetting = currentSettingData.getPayrollClientConfigurationSetting;
        if(!currentSetting) {
            setCurrentSetting(undefined);
            setCustomPayDates([]);
            return;
        }
        let payPeriodsMonthly: PayPeriodsPerMonth | undefined = undefined;
        let currentSeedDate: Date | undefined = undefined;
        const currentCustomPayDates = (currentSetting.customPayDates ?? [])
            .map(date => ensureDateInUtc(new Date(date)))
            .filter(isDefined);
        if((currentSetting.payFrequency == PayrollPayFrequency.Weekly || currentSetting.payFrequency == PayrollPayFrequency.BiWeekly)
            && currentSetting.payDateCalculationSeed
        ) {
            currentSeedDate = ensureDateInUtc(new Date(currentSetting.payDateCalculationSeed));
            const generated = generatePayDates(currentSetting.payFrequency, currentSetting.budgetYear - 1, currentSeedDate);
            payPeriodsMonthly = getPayPeriodsPerMonth(currentSetting.budgetYear - 1, generated[0]);
        }
        setCurrentSetting({
            payFrequency: currentSetting.payFrequency,
            payPeriodsMonthly: payPeriodsMonthly,
            customPayDateKeys: sortAndDedupeDates(currentCustomPayDates).map(toDateKey)
        });
        setSeedDate(currentSeedDate);
        setPayPeriodsMonthly(payPeriodsMonthly);
        setCustomPayDates(sortAndDedupeDates(currentCustomPayDates));
        setPayFrequency(currentSetting.payFrequency);
        setShowSaveConfirmationWarning(false);
    }, [currentSettingData, currentSettingLoading]);

    useEffect(() => {
        if(!budgetYear) {
            return;
        }
        if(payFrequency == PayrollPayFrequency.Custom) {
            setPayPeriodsMonthly(undefined);
            if(customPayDates.length == 0) {
                setCustomPayDates(buildCustomPresetDates(PayrollPayFrequency.Monthly, budgetYear));
            }
            return;
        }
        if(payFrequency != PayrollPayFrequency.Weekly && payFrequency != PayrollPayFrequency.BiWeekly) {
            setPayPeriodsMonthly(undefined);
            setCustomPayDates([]);
        }
        else {
            let seedDateForCalculation = seedDate;
            if(!seedDateForCalculation) {
                seedDateForCalculation = ensureDateInUtc(new Date(`${budgetYear - 2}-12-31`));
            }
            const generated = generatePayDates(payFrequency, budgetYear - 1, seedDateForCalculation);
            const payPeriodsMonthly = getPayPeriodsPerMonth(budgetYear - 1, generated[0]);
            setSeedDate(seedDateForCalculation);
            setPayPeriodsMonthly(payPeriodsMonthly);
            setCustomPayDates([]);

        }
    }, [budgetYear, payFrequency, seedDate]);

    return (
        <>
            <Grid>
                <Row className={css.settingsHeaderRow}>
                    <Col>
                        Allows setting the pay cycle frequency.
                        {payFrequency && payFrequency !== PayrollPayFrequency.Custom && <Hint>NOTE: The values set for employee's Medical, Dental and Vision benefits are multiplied by the number of pay checks per month.</Hint>}
                    </Col>
                    <Col className={css.actionButtons}>
                        <Button isDanger disabled={!haveChanges || saving} onClick={() => loadCurrent()}>Reset</Button>
                        <Button isPrimary disabled={!haveChanges || saving} onClick={() => setShowSaveConfirmationWarning(true)}>Save</Button>
                    </Col>
                </Row>
                {budgetYear &&
                    <Fragment key={budgetYear}>
                        {!payFrequency &&
                            <Row>
                                <Col>
                                    <Alert type="warning">
                                        <Title>No Payroll Configuration</Title>
                                        <Body>Payroll costs are not calculating. Please set the pay cycle.</Body>
                                    </Alert>
                                </Col>
                            </Row>
                        }
                        <Row className={css.settingsRow}>
                            <Col>
                                <Dropdown
                                    selectedItem={payFrequency}
                                    onSelect={(v) => setPayFrequency(v)}
                                >
                                    <Field>
                                        <Label>Pay Cycle</Label>
                                        <Select>
                                            {payFrequency ? payFrequencyLabel(payFrequency) : "Select Pay Cycle"}
                                        </Select>
                                    </Field>
                                    <Menu>
                                        <Item value={PayrollPayFrequency.Weekly}>{payFrequencyLabel(PayrollPayFrequency.Weekly)}</Item>
                                        <Item value={PayrollPayFrequency.BiWeekly}>{payFrequencyLabel(PayrollPayFrequency.BiWeekly)}</Item>
                                        <Item value={PayrollPayFrequency.SemiMonthly}>{payFrequencyLabel(PayrollPayFrequency.SemiMonthly)}</Item>
                                        <Item value={PayrollPayFrequency.Monthly}>{payFrequencyLabel(PayrollPayFrequency.Monthly)}</Item>
                                        <Item value={PayrollPayFrequency.Custom}>{payFrequencyLabel(PayrollPayFrequency.Custom)}</Item>
                                    </Menu>
                                </Dropdown>
                            </Col>
                        </Row>
                        {payFrequency == PayrollPayFrequency.Custom &&
                            <CustomPayDatesEditor
                                budgetYear={budgetYear}
                                monthReferences={monthReferences}
                                customDatesByMonth={customDatesByMonth}
                                setCustomPayDates={setCustomPayDates}
                                customPayDates={customPayDates}
                                presetFrequencies={customPresetFrequencies}
                            />
                        }
                        {payPeriodsMonthly ?
                            <Row className={css.settingsRow}>
                                <Col>
                                    <FormLabel>Pay Checks per Month</FormLabel>
                                    {/* Note: 3 and 5 are the only possible values for pay checks per month with one extra pay check for bi-weekly or weekly pay frequency */}
                                    <Hint>
                                        The table below shows the number of pay checks per month. Most of the time, this will be {payFrequency == PayrollPayFrequency.Weekly ? "4" : "2"}, but in some months it will be {payFrequency == PayrollPayFrequency.Weekly ? "5" : "3"}. Such months are <span className={css.extraPeriodCell}>highlighted</span> in the table. Use the buttons below to move the highlighted months to the left or right.
                                    </Hint>
                                    <br />
                                    <div className={css.moveButtons}>
                                        <Button onClick={() => seedDate ? moveSeedDate(seedDate, -1) : undefined}>Left</Button>
                                        <Button onClick={() => seedDate ? moveSeedDate(seedDate, 1) : undefined}>Right</Button>
                                    </div>
                                    <Table>
                                        <Head>
                                            <HeaderRow>
                                                <HeaderCell width="10rem">Year</HeaderCell>
                                                <HeaderCell>Jan</HeaderCell>
                                                <HeaderCell>Feb</HeaderCell>
                                                <HeaderCell>Mar</HeaderCell>
                                                <HeaderCell>Apr</HeaderCell>
                                                <HeaderCell>May</HeaderCell>
                                                <HeaderCell>Jun</HeaderCell>
                                                <HeaderCell>Jul</HeaderCell>
                                                <HeaderCell>Aug</HeaderCell>
                                                <HeaderCell>Sep</HeaderCell>
                                                <HeaderCell>Oct</HeaderCell>
                                                <HeaderCell>Nov</HeaderCell>
                                                <HeaderCell>Dec</HeaderCell>
                                            </HeaderRow>
                                        </Head>
                                        <Body>
                                            <TableRow>
                                                <Cell><b>{budgetYear - 1} Reforecast</b></Cell>
                                                {payPeriodsMonthly.reforecast.map((v, i) => <Cell className={v == 3 || v == 5 ? css.extraPeriodCell : undefined} key={i}>{v}</Cell>)}
                                            </TableRow>
                                            <TableRow>
                                                <Cell><b>{budgetYear} Budget</b></Cell>
                                                {payPeriodsMonthly.budget.map((v, i) => <Cell className={v == 3 || v == 5 ? css.extraPeriodCell : undefined} key={i}>{v}</Cell>)}
                                            </TableRow>
                                        </Body>
                                    </Table>
                                </Col>
                            </Row>
                            : payFrequency !== undefined && payFrequency != PayrollPayFrequency.Custom ?
                                <Row className={css.settingsRow}>
                                    <Col>
                                        <FormLabel>Pay Checks per Month</FormLabel>: {payFrequency == PayrollPayFrequency.Monthly ? "1" : "2"}
                                    </Col>
                                </Row> : undefined}
                    </Fragment>
                }
            </Grid>
            {showSaveConfirmationWarning &&
                <ConfirmationModal
                    onAccept={() => {setShowSaveConfirmationWarning(false); saveChanges();}}
                    onCancel={() => setShowSaveConfirmationWarning(false)}
                    header="Update payroll settings for all properties?"
                    message="The payroll settings you’re about to save will apply to every property in this workspace for the selected budget year. This will also affect any finalized budgets for that year. Are you sure you want to proceed?"
                    confirmationText="update settings"
                />
            }
        </>
    );
}